import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  Divider
} from '@mui/material';
import {
  Satellite as SatelliteIcon,
  ZoomIn as ZoomIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Cloud as CloudIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';

interface SatelliteScene {
  scene_id: string;
  acquisition_date: string;
  cloud_cover: number;
  coordinates: {
    lat: number;
    lon: number;
  };
  bands_used: string;
  composite_type: string;
  satellite?: string;
  resolution?: string;
  processing_level?: string;
  demo_mode?: boolean;
  image_size?: string;
  file_size_kb?: number;
  path?: number;
  row?: number;
}

interface SatelliteImageViewerProps {
  latitude: number;
  longitude: number;
  analysisId?: string;
  onImageLoad?: (scene: SatelliteScene) => void;
}

const SatelliteImageViewer: React.FC<SatelliteImageViewerProps> = ({
  latitude,
  longitude,
  analysisId,
  onImageLoad
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [sceneMetadata, setSceneMetadata] = useState<SatelliteScene | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const fetchSatelliteImage = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `http://localhost:8000/api/satellite/image?lat=${latitude}&lon=${longitude}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        }
      );

      if (!response.ok) {
        // Try to parse the error response for a more specific message
        try {
          const errorData = await response.json();
          const errorMessage = errorData.detail || `Failed to fetch satellite image: ${response.statusText}`;
          throw new Error(errorMessage);
        } catch (parseError) {
          // If we can't parse the response, use the status text
          throw new Error(`Failed to fetch satellite image: ${response.statusText}`);
        }
      }

      const data = await response.json();
      
      if (data.image_data && data.metadata) {
        // Create blob URL from base64 image data
        const byteCharacters = atob(data.image_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/jpeg' });
        const url = URL.createObjectURL(blob);
        
        setImageUrl(url);
        setSceneMetadata(data.metadata);
        
        if (onImageLoad) {
          onImageLoad(data.metadata);
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (latitude && longitude) {
      fetchSatelliteImage();
    }

    // Cleanup blob URL on unmount
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [latitude, longitude]);

  const handleDownload = () => {
    if (imageUrl && sceneMetadata) {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `${sceneMetadata.scene_id}_satellite.jpg`;
      link.click();
    }
  };

  const formatCoordinates = (lat: number, lon: number) => {
    const latDir = lat >= 0 ? 'N' : 'S';
    const lonDir = lon >= 0 ? 'E' : 'W';
    return `${Math.abs(lat).toFixed(3)}°${latDir}, ${Math.abs(lon).toFixed(3)}°${lonDir}`;
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Fetching satellite imagery...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    // Check if this is a network connectivity error for Sentinel-2
    const isNetworkError = error.toLowerCase().includes('network') || error.toLowerCase().includes('connectivity');
    const alertSeverity = isNetworkError ? 'warning' : 'error';
    
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity={alertSeverity} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Satellite Imagery Unavailable</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {error}
          </Typography>
          {isNetworkError && (
            <Typography variant="caption" color="text.secondary">
              This analysis uses real Sentinel-2 satellite data from ESA Copernicus Hub. 
              Network connectivity is required to access live satellite imagery.
            </Typography>
          )}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchSatelliteImage}
        >
          Retry
        </Button>
      </Paper>
    );
  }

  if (!imageUrl || !sceneMetadata) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <SatelliteIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          No satellite imagery available
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Card>
        <CardMedia
          component="img"
          sx={{
            height: 300,
            cursor: 'pointer',
            objectFit: 'cover'
          }}
          image={imageUrl}
          alt={`Satellite imagery for ${formatCoordinates(latitude, longitude)}`}
          onClick={() => setFullscreenOpen(true)}
        />
        
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" component="div">
              {sceneMetadata.satellite || 'Sentinel-2'} Scene
            </Typography>
            <Box>
              <Tooltip title="View fullscreen">
                <IconButton onClick={() => setFullscreenOpen(true)} size="small">
                  <ZoomIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download image">
                <IconButton onClick={handleDownload} size="small">
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchSatelliteImage} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                <strong>Scene ID:</strong> {sceneMetadata.scene_id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Date:</strong> {sceneMetadata.acquisition_date}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Location:</strong> {formatCoordinates(latitude, longitude)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                <strong>Composite:</strong> {sceneMetadata.bands_used}
              </Typography>
              {sceneMetadata.path && sceneMetadata.row && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Path/Row:</strong> {sceneMetadata.path}/{sceneMetadata.row}
                </Typography>
              )}
              {sceneMetadata.image_size && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Size:</strong> {sceneMetadata.image_size}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              icon={<CloudIcon />}
              label={`${sceneMetadata.cloud_cover}% Cloud`}
              size="small"
              color={sceneMetadata.cloud_cover < 20 ? 'success' : sceneMetadata.cloud_cover < 50 ? 'warning' : 'error'}
            />
            <Chip
              icon={<VisibilityIcon />}
              label={sceneMetadata.composite_type.replace('_', ' ').toUpperCase()}
              size="small"
              variant="outlined"
            />
            {sceneMetadata.demo_mode && (
              <Chip
                label="Synthetic"
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ opacity: 0.7, fontSize: '10px' }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Fullscreen Dialog */}
      <Dialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SatelliteIcon />
              <Typography variant="h6">
                Satellite Imagery - {sceneMetadata.scene_id}
              </Typography>
            </Box>
            <IconButton onClick={handleDownload}>
              <DownloadIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <img
              src={imageUrl}
              alt={`Satellite imagery for ${formatCoordinates(latitude, longitude)}`}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Scene Information
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>Scene ID:</strong> {sceneMetadata.scene_id}
                <br />
                <strong>Acquisition Date:</strong> {sceneMetadata.acquisition_date}
                <br />
                <strong>Coordinates:</strong> {formatCoordinates(latitude, longitude)}
                <br />
                <strong>Cloud Cover:</strong> {sceneMetadata.cloud_cover}%
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Technical Details
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>Bands Used:</strong> {sceneMetadata.bands_used}
                <br />
                <strong>Composite Type:</strong> {sceneMetadata.composite_type}
                <br />
                {sceneMetadata.path && sceneMetadata.row && (
                  <>
                    <strong>Path/Row:</strong> {sceneMetadata.path}/{sceneMetadata.row}
                    <br />
                  </>
                )}
                {sceneMetadata.file_size_kb && (
                  <>
                    <strong>File Size:</strong> {sceneMetadata.file_size_kb} KB
                    <br />
                  </>
                )}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setFullscreenOpen(false)}>
            Close
          </Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SatelliteImageViewer;